import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Calendar, MapPin, Clock, Mail, X } from "lucide-react";
import { exchangeApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";

const formatTime = (dateStr) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
};

const getUrgencyColor = (notificationType, daysUntil) => {
  switch (notificationType) {
    case "imminent":
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800";
    case "soon":
      return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800";
    case "today":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800";
    case "tomorrow":
      return "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-800";
    default:
      if (daysUntil === 2) {
        return "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-800";
      }
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
  }
};

const getUrgencyLabel = (notificationType, minutesUntil, daysUntil) => {
  switch (notificationType) {
    case "imminent":
      return `In ${minutesUntil} Min`;
    case "soon":
      return minutesUntil >= 60 ? `In ${Math.round(minutesUntil / 60)} Std` : `In ${minutesUntil} Min`;
    case "today":
      return "Heute";
    case "tomorrow":
      return "Morgen";
    default:
      if (daysUntil === 2) return "Übermorgen";
      if (typeof daysUntil === "number" && daysUntil > 2) return `In ${daysUntil} Tagen`;
      return "Demnächst";
  }
};

export function UpcomingEventsNotification({ hoursAhead = 24 }) {
  const toast = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);

  const fetchUpcomingEvents = useCallback(async () => {
    try {
      const response = await exchangeApi.getUpcomingEvents(hoursAhead);
      setEvents(response.data.events || []);
    } catch (error) {
      // Silently fail if Exchange is not connected
      console.log("Could not fetch upcoming events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [hoursAhead]);

  useEffect(() => {
    fetchUpcomingEvents();
    // Refresh every 5 minutes
    const interval = setInterval(fetchUpcomingEvents, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUpcomingEvents]);

  const handleDismiss = (eventId) => {
    setDismissed([...dismissed, eventId]);
  };

  const handleSendReminder = async (event) => {
    setSendingReminder(event.id);
    try {
      // Get current user email from localStorage or use a default
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const recipientEmail = user?.email || "";

      if (!recipientEmail) {
        toast.error("Keine E-Mail-Adresse gefunden");
        return;
      }

      await exchangeApi.sendCalendarReminder(
        event.subject,
        event.start,
        event.location,
        recipientEmail
      );
      toast.success("Erinnerung gesendet");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSendingReminder(null);
    }
  };

  // Filter out dismissed events
  const visibleEvents = events.filter((e) => !dismissed.includes(e.id));

  if (loading || visibleEvents.length === 0) {
    return null;
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          Anstehende Termine
          <Badge variant="secondary" className="ml-auto">
            {visibleEvents.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {visibleEvents.slice(0, 5).map((event) => (
            <div
              key={event.id}
              className="flex items-start justify-between p-3 bg-white dark:bg-slate-800/60 rounded-lg border shadow-sm"
              data-testid={`upcoming-event-${event.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={getUrgencyColor(event.notification_type, event.days_until)}>
                    {getUrgencyLabel(event.notification_type, event.minutes_until, event.days_until)}
                  </Badge>
                  {event.is_recurring && (
                    <Badge variant="outline" className="text-xs">
                      Wiederkehrend
                    </Badge>
                  )}
                </div>
                <h4 className="font-medium truncate">{event.subject}</h4>
                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(event.start)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime(event.start)}
                  </span>
                  {event.location && (
                    <span className="flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3" />
                      {event.location}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSendReminder(event)}
                  disabled={sendingReminder === event.id}
                  title="E-Mail-Erinnerung senden"
                >
                  <Mail className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismiss(event.id)}
                  title="Ausblenden"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          {visibleEvents.length > 5 && (
            <p className="text-sm text-center text-muted-foreground">
              +{visibleEvents.length - 5} weitere Termine
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
